import { useState, useMemo } from "react";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js";
import LoadingSpinner from "components/LoadingSpinner";
import { Container } from "reactstrap";

import { chartOptions, parseOptions } from "variables/charts.js";

import { api } from "../config/middleware";
import { API_GETSETTING } from "../config/api";
import { useSessionStore } from "../stores/sessionStore";

/* ===== SVG ICONS (CRA) ===== */
import { ReactComponent as HomeIcon } from "../assets/icons/house-svgrepo-com.svg";
import { ReactComponent as CustomerIcon } from "../assets/icons/man-svgrepo-com.svg";
import { ReactComponent as ProductIcon } from "../assets/icons/box-svgrepo-com.svg";
import { ReactComponent as ServiceIcon } from "../assets/icons/heart-svgrepo-com.svg";
import { ReactComponent as BillIcon } from "../assets/icons/shopping-card-svgrepo-com.svg";
import { ReactComponent as OrgIcon } from "../assets/icons/building-svgrepo-com.svg";
import { ReactComponent as BranchIcon } from "../assets/icons/shop-svgrepo-com.svg";
import { ReactComponent as EmployeeIcon } from "../assets/icons/id-card-svgrepo-com.svg";
import { ReactComponent as SettingIcon } from "../assets/icons/wrench-svgrepo-com.svg";
import { ReactComponent as MarketPlaceIcon } from "../assets/icons/shopping-bag-svgrepo-com.svg";
import { ReactComponent as ReportIcon } from "../assets/icons/file-2-svgrepo-com.svg";

/* ===== TOP ICON MENU ===== */
const TopIconMenu = ({
  items,
  activeKey,
  onChange,
  onNavigate,
  disabledKey,
}) => (
  <div className="zi-topmenu">
    <div className="zi-topmenu__scroll">
      {items.map((it) => {
        const Icon = it.icon;
        const active = it.key === activeKey;
        const disabled = disabledKey === it.key;

        return (
          <button
            key={it.key}
            type="button"
            className={`zi-topmenu__item ${active ? "is-active" : ""} ${
              disabled ? "is-disabled" : ""
            }`}
            style={{ "--zi-accent": it.color }}
            onClick={() => {
              if (disabled) return;
              onChange(it.key);
              onNavigate(it.path, it.key);
            }}
            disabled={disabled}
          >
            {Icon ? (
              <span className="zi-topmenu__icon">
                <Icon className="zi-topmenu__svg" />
              </span>
            ) : null}

            <span className="zi-topmenu__label">{it.label}</span>
          </button>
        );
      })}
    </div>

    <style>{`
.zi-topmenu{ padding:0 !important; margin-bottom:0; width:100%; display:flex; justify-content:center; }
.zi-topmenu__scroll{ display:flex; flex-wrap:wrap; gap:16px; width:100%; max-width:1200px; justify-content:center; }

.zi-topmenu__item{
  flex:0 0 calc(33.333% - 16px);
  height:130px;
  border-radius:18px;
  background: color-mix(in srgb, var(--zi-accent) 80%, white);
  border:none;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:10px;
  cursor:pointer;
  transition: all .15s ease;
}
.zi-topmenu__item:hover{ filter:brightness(.95); transform:translateY(-2px); box-shadow:0 14px 26px rgba(0,0,0,.15); }

.zi-topmenu__item.is-disabled{
  opacity: .75;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
  filter: none !important;
}

.zi-topmenu__icon{ width:52px; height:52px; display:flex; align-items:center; justify-content:center; color:#fff; }
.zi-topmenu__svg{ width:38px !important; height:38px !important; }

.zi-topmenu__label{ font-size:18px; font-weight:600; color:#fff; text-align:center; line-height:1.2; white-space:nowrap; }

.zi-topmenu__item.is-active{ outline:3px solid rgba(255,255,255,.35); box-shadow:0 18px 32px rgba(0,0,0,.25); }

@media (max-width: 991.98px){
  .zi-topmenu__item{ flex:0 0 calc(50% - 16px); height:120px; }
}
@media (max-width: 575.98px){
  .zi-topmenu__item{ flex:0 0 100%; height:110px; }
}
`}</style>
  </div>
);

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const role = (user?.role || localStorage.getItem("role") || "user")
    .toLowerCase()
    .trim();
  const isAdmin = role === "admin";

  const brId = useSessionStore((s) => s.profile?.brId);

  const [activeCategory, setActiveCategory] = useState("box");
  const [checkingBill, setCheckingBill] = useState(false);

  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  const goOpenBill = async () => {
    if (checkingBill) return;
    setCheckingBill(true);

    try {
      const res = await api.get(API_GETSETTING, { params: { BRID: brId } });

      const billMode = Number(res?.data?.data?.billMode );

      navigate(billMode === 1 ? "/order" : "/order-bill-org");
    } catch (err) {
      console.error("GetSetting error:", err?.response?.data || err);
      alert("ไม่สามารถตรวจสอบการตั้งค่าเปิดบิลได้");
    } finally {
      setCheckingBill(false);
    }
  };

  const adminMenuItems = [
    {
      key: "box",
      label: "สินค้า",
      icon: ProductIcon,
      path: "/product",
      color: "#0099FF",
    },
    {
      key: "service",
      label: "บริการ",
      icon: ServiceIcon,
      path: "/manage-package",
      color: "#FF99CC",
    },
    {
      key: "sale",
      label: "เปิดบิล",
      icon: BillIcon,
      path: "#",
      color: "#22C55E",
    },
    {
      key: "report",
      label: "รายงาน",
      icon: ReportIcon,
      path: "#",
      color: "#6366F1",
    },
    {
      key: "setting",
      label: "ตั้งค่า",
      icon: SettingIcon,
      path: "/submenu-product-service",
      color: "#A9A9A9",
    },
    {
      key: "marketplace",
      label: "Marketplace",
      icon: MarketPlaceIcon,
      path: "/marketplace",
      color: "#F59E0B",
    },
  ];

  const userMenuItems = [
    {
      key: "box",
      label: "สินค้า",
      icon: ProductIcon,
      path: "/product",
      color: "#0099FF",
    },
    {
      key: "service",
      label: "บริการ",
      icon: ServiceIcon,
      path: "/manage-package",
      color: "#FF99CC",
    },
    {
      key: "sale",
      label: "เปิดบิล",
      icon: BillIcon,
      path: "#",
      color: "#22C55E",
    },
    {
      key: "report",
      label: "รายงาน",
      icon: ReportIcon,
      path: "#",
      color: "#6366F1",
    },
    {
      key: "setting",
      label: "ตั้งค่า",
      icon: SettingIcon,
      path: "/submenu-product-service",
      color: "#A9A9A9",
    },
    {
      key: "marketplace",
      label: "Marketplace",
      icon: MarketPlaceIcon,
      path: "/marketplace",
      color: "#F59E0B",
    },
  ];

  const topMenuItems = useMemo(() => {
    return [...(isAdmin ? adminMenuItems : userMenuItems)];
  }, [isAdmin]);

  const handleNavigate = (path, key) => {
    if (key === "sale") return goOpenBill();
    if (path && path !== "#") navigate(path);
  };

  if (checkingBill) return <LoadingSpinner fullPage />;

  return (
    <>
      <div
        className="header"
        style={{
          minHeight: "100vh",
          background: "#f7fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container fluid style={{ display: "flex", justifyContent: "center" }}>
          <TopIconMenu
            items={topMenuItems}
            activeKey={activeCategory}
            onChange={setActiveCategory}
            onNavigate={handleNavigate}
            disabledKey={checkingBill ? "sale" : null}
          />
        </Container>
      </div>
    </>
  );
};

export default Index;
